/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";
import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  DollarSign,
  Layers,
  Truck,
  HardHat,
  FolderOpen,
  Wrench,
  Calculator,
  Bot,
  Briefcase,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Cost Database",
  title: "Bid with numbers",
  titleHighlight: "you can trust.",
  description:
    "Your margin lives or dies on your cost data. Keep materials, labor, equipment, supplier pricing, and reusable work packages in one system so every estimate starts from real numbers.",

  featureGrid: {
    title: "Everything in your cost system",
    items: [
      {
        icon: DollarSign,
        title: "Material Pricing You Control",
        description:
          "Track material prices by category and update quickly when supplier pricing changes.",
      },
      {
        icon: HardHat,
        title: "Labor Rates by Task",
        description:
          "Set labor assumptions by task so bids reflect how your crews actually perform in the field.",
      },
      {
        icon: Wrench,
        title: "Equipment Cost Coverage",
        description:
          "Include owned and rented equipment costs so heavy-use jobs are priced correctly.",
      },
      {
        icon: Layers,
        title: "Reusable Work Packages",
        description:
          "Build reusable assembly templates for repeat scopes so your team stops rebuilding the same estimate sections from scratch.",
      },
      {
        icon: FolderOpen,
        title: "Category Structure That Scales",
        description:
          "Organize costs by category so review, reporting, and pricing updates stay manageable as your catalog grows.",
      },
      {
        icon: Truck,
        title: "Supplier Context",
        description:
          "Keep supplier terms and contacts tied to the costs they influence so purchasing and estimating stay aligned.",
      },
    ],
  },

  workflow: {
    title: "How your cost database works",
    description:
      "Build a dependable pricing foundation, reuse it on every bid, and improve it with real project outcomes.",
    steps: [
      {
        label: "Build",
        title: "Set up your baseline costs",
        description:
          "Start with your current pricing and organize it into one reliable company-wide source of truth.",
        details: [
          "Import-friendly setup from existing catalogs and spreadsheets",
          "Category-based organization across trades",
          "Labor, material, and equipment alignment",
          "Consistent company-wide pricing baseline",
        ],
      },
      {
        label: "Package",
        title: "Create repeatable scopes",
        description:
          "Bundle repeat scopes into reusable packages so estimators build faster without missing key components.",
        details: [
          "Reusable package building for common work types",
          "Standardized inputs for repeat scopes",
          "Faster estimate assembly",
          "Lower line-item omission risk",
        ],
      },
      {
        label: "Estimate",
        title: "Price jobs from your real data",
        description:
          "Use current catalog data in estimates so proposals are grounded in current costs, not stale spreadsheets.",
        details: [
          "Fast lookup of catalog items during estimating",
          "Package expansion into estimate line items",
          "Category-aware pricing support",
          "Job-specific pricing flexibility when needed",
        ],
      },
      {
        label: "Improve",
        title: "Use real outcomes to tighten future bids",
        description:
          "Use completed-job outcomes to improve your pricing so each next estimate starts from better numbers.",
        details: [
          "Estimate-vs-actual variance visibility",
          "Category-level cost drift awareness",
          "Supplier trend visibility",
          "Continuous estimate quality improvement",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Layers,
      title: "Build Once, Reuse On Every Job",
      description:
        "Standardize repeat scopes so estimators move faster and maintain consistency across the team.",
      bullets: [
        "Build reusable templates that pull current material, labor, and equipment pricing when applied",
        "Scope-level consistency for repeat work",
        "Faster estimate assembly",
        "Reduced missed-line-item risk",
      ],
    },
    {
      icon: Truck,
      title: "Supplier-Driven Pricing Confidence",
      description:
        "Keep supplier details and terms close to pricing decisions so buying assumptions stay realistic.",
      bullets: [
        "Supplier-linked pricing visibility",
        "Contact and terms in one place",
        "Purchasing context tied to estimating",
        "Better purchasing confidence",
      ],
    },
    {
      icon: HardHat,
      title: "Equipment Costs That Protect Margin",
      description:
        "Include equipment costs in every relevant scope so heavy-use jobs do not quietly burn margin.",
      bullets: [
        "Equipment rate tracking support",
        "Operational cost context in estimating",
        "More realistic project pricing",
        "Stronger margin protection",
      ],
    },
    {
      icon: DollarSign,
      title: "Pricing Defaults With Smart Flexibility",
      description:
        "Keep estimating consistent with default pricing rules while allowing controlled overrides where the deal requires it.",
      bullets: [
        "Pricing cascades from organization defaults down to division, budget, and line-item decisions",
        "Override at the level you need without losing your baseline",
        "More consistent estimating behavior",
        "Controlled exception handling",
      ],
    },
  ],

  connections: {
    title: "How cost data drives better execution",
    items: [
      {
        icon: Calculator,
        flow: "Costs → Estimates",
        description:
          "Cost data drives estimate quality from first draft to final proposal.",
      },
      {
        icon: Briefcase,
        flow: "Projects → Actuals",
        description:
          "Completed job outcomes feed back into better pricing decisions.",
      },
      {
        icon: Truck,
        flow: "Suppliers → Pricing",
        description:
          "Supplier inputs keep future buying assumptions grounded in reality.",
      },
      {
        icon: DollarSign,
        flow: "Actual Costs → Future Bids",
        description:
          "As real costs come in from completed work, future bids get tighter and more profitable.",
      },
      {
        icon: Layers,
        flow: "Work Packages → Faster Estimating",
        description:
          "Reusable work packages turn repeat scopes into fast, consistent estimate building blocks.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: Bot,
    },
    {
      label: "Projects & Services",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Purchase Orders",
      href: "/features/purchase-orders",
      icon: Truck,
    },
    {
      label: "Subcontractors",
      href: "/features/subcontractors",
      icon: Wrench,
    },
  ],
};

export function CostDatabasePageClient() {
  return <FeaturePageTemplate config={config} />;
}
